import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  InputAdornment,
} from "@mui/material";
import {
  Palette as PaletteIcon,
  Language as LanguageIcon,
  Business as BusinessIcon,
  Notifications as NotificationsIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {trackFeatureAccess} from "../../utils/analytics";
import {toggleAlerts, getAlertConfig, type AlertConfig} from "../../api/config";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`config-tabpanel-${index}`} aria-labelledby={`config-tab-${index}`} {...other}>
      {value === index && <Box sx={{p: 3}}>{children}</Box>}
    </div>
  );
}

const ConfigPage: React.FC = () => {
  const {t, i18n} = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{type: "success" | "error"; text: string} | null>(null);

  // UI Theme Configuration
  const [uiTheme, setUiTheme] = useState({
    primaryColor: "#1976d2",
    secondaryColor: "#dc004e",
    backgroundColor: "#f5f5f5",
    textColor: "#333333",
    borderRadius: "8px",
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
  });

  // Business Rules Configuration
  const [businessRules, setBusinessRules] = useState({
    feedConversionRatio: {
      min: 1.0,
      max: 3.0,
      warning: 2.5,
    },
    mortalityRate: {
      max: 10.0,
      warning: 5.0,
    },
    waterQuality: {
      temperature: {
        min: 20,
        max: 32,
        warning: {min: 22, max: 30},
      },
      ph: {
        min: 6.5,
        max: 8.5,
        warning: {min: 7.0, max: 8.0},
      },
      dissolvedOxygen: {
        min: 4.0,
        warning: 5.0,
      },
      ammonia: {
        max: 1.0,
        warning: 0.5,
      },
      nitrite: {
        max: 0.5,
        warning: 0.1,
      },
    },
  });

  // Alert Configuration
  const [alerts, setAlerts] = useState({
    waterQuality: {
      enabled: true,
      checkFrequency: "daily",
      notifications: ["email", "sms"],
    },
    mortality: {
      enabled: true,
      threshold: 5.0,
      notifications: ["email", "sms"],
    },
    feedInventory: {
      enabled: true,
      lowStockThreshold: 100,
      notifications: ["email"],
    },
    maintenance: {
      enabled: true,
      reminderDays: 7,
      notifications: ["email"],
    },
  });

  // Global alert configuration
  const [globalAlertsEnabled, setGlobalAlertsEnabled] = useState(true);

  const configTabNames = ["ui_theme", "labels", "business_rules", "alerts"];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    trackFeatureAccess(`config_tab_${configTabNames[newValue] || "unknown"}`, {
      tab_index: newValue,
    });
  };

  // Load alert configuration on component mount
  useEffect(() => {
    const loadAlertConfig = async () => {
      try {
        const config = await getAlertConfig();
        setGlobalAlertsEnabled(config.enabled);
      } catch (error) {
        console.error("Error loading alert config:", error);
      }
    };
    loadAlertConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save alert configuration
      await toggleAlerts(globalAlertsEnabled);
      setMessage({type: "success", text: "Configuration saved successfully!"});
    } catch (error) {
      setMessage({type: "error", text: "Failed to save configuration"});
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Reset to default values
    setMessage({type: "success", text: "Configuration reset to defaults"});
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t("Configuration")}</Typography>
        <Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset} sx={{mr: 2}}>
            {t("Reset to Defaults")}
          </Button>
          <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {saving ? t("Saving...") : t("Save Configuration")}
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{mb: 3}} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{width: "100%"}}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="configuration tabs" sx={{borderBottom: 1, borderColor: "divider"}}>
          <Tab icon={<PaletteIcon />} label={t("UI Theme")} iconPosition="start" />
          <Tab icon={<LanguageIcon />} label={t("Labels")} iconPosition="start" />
          <Tab icon={<BusinessIcon />} label={t("Business Rules")} iconPosition="start" />
          <Tab icon={<NotificationsIcon />} label={t("Alerts")} iconPosition="start" />
        </Tabs>

        {/* UI Theme Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {t("Color Scheme")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Primary Color")}
                    type="color"
                    value={uiTheme.primaryColor}
                    onChange={(e) => setUiTheme({...uiTheme, primaryColor: e.target.value})}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">#</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Secondary Color")}
                    type="color"
                    value={uiTheme.secondaryColor}
                    onChange={(e) => setUiTheme({...uiTheme, secondaryColor: e.target.value})}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">#</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Background Color")}
                    type="color"
                    value={uiTheme.backgroundColor}
                    onChange={(e) => setUiTheme({...uiTheme, backgroundColor: e.target.value})}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">#</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Text Color")}
                    type="color"
                    value={uiTheme.textColor}
                    onChange={(e) => setUiTheme({...uiTheme, textColor: e.target.value})}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">#</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {t("Typography")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>{t("Font Family")}</InputLabel>
                    <Select value={uiTheme.fontFamily} onChange={(e) => setUiTheme({...uiTheme, fontFamily: e.target.value})}>
                      <MenuItem value="Roboto, sans-serif">Roboto</MenuItem>
                      <MenuItem value="Arial, sans-serif">Arial</MenuItem>
                      <MenuItem value="Helvetica, sans-serif">Helvetica</MenuItem>
                      <MenuItem value="Times New Roman, serif">Times New Roman</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Font Size")}
                    value={uiTheme.fontSize}
                    onChange={(e) => setUiTheme({...uiTheme, fontSize: e.target.value})}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Border Radius")}
                    value={uiTheme.borderRadius}
                    onChange={(e) => setUiTheme({...uiTheme, borderRadius: e.target.value})}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Labels Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            {t("Language Settings")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Default Language")}</InputLabel>
                <Select value={i18n.language} onChange={(e) => i18n.changeLanguage(e.target.value)}>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Español</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                {t("Custom labels can be configured here. Changes will apply to the entire application.")}
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Business Rules Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {t("Feed Conversion Ratio")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label={t("Min")}
                    type="number"
                    value={businessRules.feedConversionRatio.min}
                    onChange={(e) =>
                      setBusinessRules({
                        ...businessRules,
                        feedConversionRatio: {
                          ...businessRules.feedConversionRatio,
                          min: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label={t("Max")}
                    type="number"
                    value={businessRules.feedConversionRatio.max}
                    onChange={(e) =>
                      setBusinessRules({
                        ...businessRules,
                        feedConversionRatio: {
                          ...businessRules.feedConversionRatio,
                          max: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label={t("Warning")}
                    type="number"
                    value={businessRules.feedConversionRatio.warning}
                    onChange={(e) =>
                      setBusinessRules({
                        ...businessRules,
                        feedConversionRatio: {
                          ...businessRules.feedConversionRatio,
                          warning: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {t("Mortality Rate")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Max %")}
                    type="number"
                    value={businessRules.mortalityRate.max}
                    onChange={(e) =>
                      setBusinessRules({
                        ...businessRules,
                        mortalityRate: {
                          ...businessRules.mortalityRate,
                          max: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Warning %")}
                    type="number"
                    value={businessRules.mortalityRate.warning}
                    onChange={(e) =>
                      setBusinessRules({
                        ...businessRules,
                        mortalityRate: {
                          ...businessRules.mortalityRate,
                          warning: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">{t("Water Quality Parameters")}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t("Temperature (°C)")}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField fullWidth size="small" label={t("Min")} type="number" value={businessRules.waterQuality.temperature.min} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField fullWidth size="small" label={t("Max")} type="number" value={businessRules.waterQuality.temperature.max} />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t("pH")}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField fullWidth size="small" label={t("Min")} type="number" value={businessRules.waterQuality.ph.min} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField fullWidth size="small" label={t("Max")} type="number" value={businessRules.waterQuality.ph.max} />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t("Dissolved Oxygen (mg/L)")}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField fullWidth size="small" label={t("Min")} type="number" value={businessRules.waterQuality.dissolvedOxygen.min} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t("Warning")}
                            type="number"
                            value={businessRules.waterQuality.dissolvedOxygen.warning}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Alerts Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{t("Global Alert Settings")}</Typography>
                    <FormControlLabel
                      control={<Switch checked={globalAlertsEnabled} onChange={(e) => setGlobalAlertsEnabled(e.target.checked)} />}
                      label={t("Enable All Alerts")}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {t("Toggle this switch to enable or disable all system alerts including water quality, maintenance, and feed stock alerts.")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{t("Water Quality Alerts")}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={alerts.waterQuality.enabled}
                          onChange={(e) =>
                            setAlerts({
                              ...alerts,
                              waterQuality: {
                                ...alerts.waterQuality,
                                enabled: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label=""
                    />
                  </Box>
                  <FormControl fullWidth size="small" sx={{mb: 2}}>
                    <InputLabel>{t("Check Frequency")}</InputLabel>
                    <Select
                      value={alerts.waterQuality.checkFrequency}
                      onChange={(e) =>
                        setAlerts({
                          ...alerts,
                          waterQuality: {
                            ...alerts.waterQuality,
                            checkFrequency: e.target.value,
                          },
                        })
                      }>
                      <MenuItem value="hourly">{t("Hourly")}</MenuItem>
                      <MenuItem value="daily">{t("Daily")}</MenuItem>
                      <MenuItem value="weekly">{t("Weekly")}</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="textSecondary">
                    {t("Notifications:")}{" "}
                    {alerts.waterQuality.notifications.map((n) => (
                      <Chip key={n} label={n} size="small" sx={{mr: 1}} />
                    ))}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{t("Mortality Alerts")}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={alerts.mortality.enabled}
                          onChange={(e) =>
                            setAlerts({
                              ...alerts,
                              mortality: {
                                ...alerts.mortality,
                                enabled: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label=""
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("Threshold %")}
                    type="number"
                    value={alerts.mortality.threshold}
                    onChange={(e) =>
                      setAlerts({
                        ...alerts,
                        mortality: {
                          ...alerts.mortality,
                          threshold: parseFloat(e.target.value),
                        },
                      })
                    }
                    sx={{mb: 2}}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {t("Notifications:")}{" "}
                    {alerts.mortality.notifications.map((n) => (
                      <Chip key={n} label={n} size="small" sx={{mr: 1}} />
                    ))}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{t("Feed Inventory Alerts")}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={alerts.feedInventory.enabled}
                          onChange={(e) =>
                            setAlerts({
                              ...alerts,
                              feedInventory: {
                                ...alerts.feedInventory,
                                enabled: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label=""
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("Low Stock Threshold (kg)")}
                    type="number"
                    value={alerts.feedInventory.lowStockThreshold}
                    onChange={(e) =>
                      setAlerts({
                        ...alerts,
                        feedInventory: {
                          ...alerts.feedInventory,
                          lowStockThreshold: parseInt(e.target.value),
                        },
                      })
                    }
                    sx={{mb: 2}}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {t("Notifications:")}{" "}
                    {alerts.feedInventory.notifications.map((n) => (
                      <Chip key={n} label={n} size="small" sx={{mr: 1}} />
                    ))}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{t("Maintenance Alerts")}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={alerts.maintenance.enabled}
                          onChange={(e) =>
                            setAlerts({
                              ...alerts,
                              maintenance: {
                                ...alerts.maintenance,
                                enabled: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label=""
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("Reminder Days")}
                    type="number"
                    value={alerts.maintenance.reminderDays}
                    onChange={(e) =>
                      setAlerts({
                        ...alerts,
                        maintenance: {
                          ...alerts.maintenance,
                          reminderDays: parseInt(e.target.value),
                        },
                      })
                    }
                    sx={{mb: 2}}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {t("Notifications:")}{" "}
                    {alerts.maintenance.notifications.map((n) => (
                      <Chip key={n} label={n} size="small" sx={{mr: 1}} />
                    ))}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default ConfigPage;
